import React, { memo } from 'react';
import { Card } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';

interface SearchHistoryItem {
  id: string;
  search_type: string;
  search_value: string;
  searched_at: string;
  latitude?: number | null;
  longitude?: number | null;
  city?: string | null;
  address?: string | null;
  country?: string | null;
  region?: string | null;
}

interface SearchHistoryProps {
  searchHistory: SearchHistoryItem[];
  isLoadingHistory: boolean;
  totalSearches: number;
  uniqueSearches: number;
  showSearchHistory?: boolean;
  onShowSearchHistory?: () => void;
}

export const SearchHistory = memo(({
  searchHistory,
  isLoadingHistory,
  totalSearches,
  uniqueSearches,
  showSearchHistory = false,
  onShowSearchHistory,
}: SearchHistoryProps) => {
  return (
    <Card className="bg-card border border-border shadow-sm mb-6 p-6">
      <h3 className="text-lg font-semibold text-foreground mb-2">Search History</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 pb-4 border-b border-border">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-foreground">Total Searches (all-time):</span>
          <span className="text-lg font-semibold text-foreground">{totalSearches ?? 0}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-foreground">Unique Searches:</span>
          <span className="text-lg font-semibold text-foreground">{uniqueSearches ?? 0}</span>
        </div>
      </div>
      {!showSearchHistory && onShowSearchHistory ? (
        <div className="flex justify-center py-4">
          <button
            onClick={onShowSearchHistory}
            className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg font-medium transition-colors"
          >
            Show Search History
          </button>
        </div>
      ) : isLoadingHistory ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          <span className="ml-2 text-sm text-muted-foreground">Loading history...</span>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {searchHistory.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No recent search history available.</p>
            ) : searchHistory.map((item) => {
              const date = new Date(item.searched_at);
              const formattedDate = date.toLocaleDateString('en-IN', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric',
                timeZone: 'Asia/Kolkata'
              });
              const formattedTime = date.toLocaleTimeString('en-IN', { 
                hour: '2-digit', 
                minute: '2-digit',
                hour12: true,
                timeZone: 'Asia/Kolkata'
              });
              
              return (
                <div key={item.id} className="flex items-center justify-between p-3 bg-card/50 rounded-lg border border-border">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-medium text-foreground/80 uppercase">
                        {item.search_type === 'metro_card' 
                          ? 'Metro Card' 
                          : item.search_type === 'vehicle_number'
                          ? 'Vehicle Number'
                          : 'Metefy Token'}
                      </span>
                      <span className="text-sm font-mono text-foreground">{item.search_value}</span>
                    </div>
                    <div className="text-xs text-foreground/80 mb-1">
                      {formattedDate} at {formattedTime} IST
                    </div>
                    {(item.city || item.address) && (
                      <div className="text-xs text-primary font-medium">
                        📍 {item.city || item.address}
                        {item.latitude && item.longitude && (
                          <a 
                            href={`https://www.google.com/maps?q=${item.latitude},${item.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-2 hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            (View on Map)
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
});

SearchHistory.displayName = 'SearchHistory';


